import React, { useState, useEffect } from "react";
import { UserService } from "../services/userService";
import type {
  ForgotPasswordRequest,
  ForgotPasswordResponse,
} from "../types/auth";
import pabLoginLogo from "../images/pab-login-logo.webp";

interface ForgotPasswordProps {
  onBackToLogin?: () => void;
}

const MAX_RESEND_ATTEMPTS = 3;
const LOCKOUT_MINUTES = 60;
const COUNTDOWN_SECONDS = 60;

export const ForgotPassword: React.FC<ForgotPasswordProps> = ({
  onBackToLogin,
}) => {
  const [stage, setStage] = useState<"email" | "code" | "password">("email");
  const [formData, setFormData] = useState<ForgotPasswordRequest>({
    EmailId: "",
    ValidationCode: "",
    Password: "",
    ConfirmPassword: "",
  });
  const [message, setMessage] = useState("");
  const [messageType, setMessageType] = useState<"success" | "error" | "">(
    ""
  );
  const [isLoading, setIsLoading] = useState(false);
  const [resendAttempts, setResendAttempts] = useState(0);
  const [isLockedOut, setIsLockedOut] = useState(false);
  const [lockoutUntil, setLockoutUntil] = useState<Date | null>(null);
  const [secondsLeft, setSecondsLeft] = useState(COUNTDOWN_SECONDS);
  const [isCountingDown, setIsCountingDown] = useState(false);

  // Check lockout status on mount
  useEffect(() => {
    checkIsLocked();
    // Load resend attempts from localStorage
    const storedAttempts = parseInt(
      localStorage.getItem("resend_attempt_count") || "0"
    );
    setResendAttempts(storedAttempts);
  }, []);

  // Countdown timer effect
  useEffect(() => {
    if (isCountingDown && secondsLeft > 0) {
      const timer = setTimeout(() => {
        setSecondsLeft((prev) => prev - 1);
      }, 1000);
      return () => clearTimeout(timer);
    } else if (isCountingDown && secondsLeft === 0) {
      setIsCountingDown(false);
    }
  }, [isCountingDown, secondsLeft]);

  // Check lockout status
  const checkIsLocked = (): boolean => {
    const storedLockout = localStorage.getItem("resend_lockout_time");
    if (storedLockout) {
      const lockoutTime = new Date(storedLockout);
      if (new Date() < lockoutTime) {
        setLockoutUntil(lockoutTime);
        setIsLockedOut(true);
        return true;
      } else {
        // Lockout expired, clear it
        localStorage.removeItem("resend_lockout_time");
        localStorage.removeItem("resend_attempt_count");
        setIsLockedOut(false);
        setLockoutUntil(null);
        setResendAttempts(0);
        return false;
      }
    }
    return false;
  };

  // Start countdown timer
  const startCountdown = () => {
    setIsCountingDown(true);
    setSecondsLeft(COUNTDOWN_SECONDS);
  };

  // Email validation
  const isValidEmail = (email: string): boolean => {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  };

  // Step 1: Request OTP
  const handleRequestOTP = async () => {
    if (!formData.EmailId.trim()) {
      setMessage("Please enter your email address");
      setMessageType("error");
      return;
    }

    // Validate email format
    if (!isValidEmail(formData.EmailId)) {
      setMessage("Please enter a valid email address");
      setMessageType("error");
      return;
    }

    // Check rate limiting
    if (checkIsLocked()) {
      const minutesLeft = Math.ceil(
        (lockoutUntil!.getTime() - new Date().getTime()) / (1000 * 60)
      );
      setMessage(
        `Too many attempts. Please try again after ${minutesLeft} minute${
          minutesLeft > 1 ? "s" : ""
        }.`
      );
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await UserService.ForgotPassword({
        EmailId: formData.EmailId,
      });
      const result: ForgotPasswordResponse = JSON.parse(response);

      if (result.success) {
        setStage("code");
        const newAttempts = resendAttempts + 1;
        setResendAttempts(newAttempts);
        localStorage.setItem("resend_attempt_count", String(newAttempts));
        startCountdown();
        setMessage("OTP sent to your email. Please check your inbox.");
        setMessageType("success");
      } else {
        if (result.message.includes("limit reached")) {
          const lockoutUntil = new Date();
          lockoutUntil.setMinutes(lockoutUntil.getMinutes() + LOCKOUT_MINUTES);
          localStorage.setItem(
            "resend_lockout_time",
            lockoutUntil.toISOString()
          );
          setLockoutUntil(lockoutUntil);
          setIsLockedOut(true);
        }
        setMessage(result.message || "Failed to send OTP. Please try again.");
        setMessageType("error");
      }
    } catch (error) {
      console.error("Request OTP error:", error);
      setMessage("An error occurred. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 2: Validate OTP
  const handleValidateOTP = async () => {
    if (!formData.ValidationCode?.trim()) {
      setMessage("Please enter the OTP code");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await UserService.ValidateCode({
        EmailId: formData.EmailId,
        ValidationCode: formData.ValidationCode,
      });
      const result: ForgotPasswordResponse = JSON.parse(response);

      if (result.success) {
        setStage("password");
        setMessage("");
        setMessageType("");
      } else {
        setMessage(
          result.message || "Invalid OTP code. Please try again."
        );
        setMessageType("error");
      }
    } catch (error) {
      console.error("Validate OTP error:", error);
      setMessage("An error occurred. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Step 3: Reset Password
  const handleResetPassword = async () => {
    if (!formData.Password || !formData.ConfirmPassword) {
      setMessage("Please enter and confirm your new password");
      setMessageType("error");
      return;
    }

    if (formData.Password !== formData.ConfirmPassword) {
      setMessage("Passwords do not match");
      setMessageType("error");
      return;
    }

    if (formData.Password.length < 6) {
      setMessage("Password must be at least 6 characters");
      setMessageType("error");
      return;
    }

    setIsLoading(true);
    setMessage("");
    setMessageType("");

    try {
      const response = await UserService.ResetPassword({
        EmailId: formData.EmailId,
        Password: formData.Password,
      });
      const result: ForgotPasswordResponse = JSON.parse(response);

      if (result.success) {
        setMessage("Password reset successfully! Redirecting to login...");
        setMessageType("success");
        // Clear localStorage
        localStorage.removeItem("resend_attempt_count");
        localStorage.removeItem("resend_lockout_time");
        // Redirect to login after 2 seconds
        setTimeout(() => {
          if (onBackToLogin) {
            onBackToLogin();
          } else {
            window.location.href = "/";
          }
        }, 2000);
      } else {
        setMessage(
          result.message || "Failed to reset password. Please try again."
        );
        setMessageType("error");
      }
    } catch (error) {
      console.error("Reset password error:", error);
      setMessage("An error occurred. Please try again.");
      setMessageType("error");
    } finally {
      setIsLoading(false);
    }
  };

  // Resend OTP
  const handleResendOTP = async () => {
    if (isCountingDown) {
      return;
    }

    if (checkIsLocked()) {
      const minutesLeft = Math.ceil(
        (lockoutUntil!.getTime() - new Date().getTime()) / (1000 * 60)
      );
      setMessage(
        `Too many attempts. Please try again after ${minutesLeft} minute${
          minutesLeft > 1 ? "s" : ""
        }.`
      );
      setMessageType("error");
      return;
    }

    if (resendAttempts >= MAX_RESEND_ATTEMPTS) {
      setMessage(
        `Maximum resend attempts reached. Please wait ${LOCKOUT_MINUTES} minutes.`
      );
      setMessageType("error");
      return;
    }

    await handleRequestOTP();
  };

  // Icon Components
  const EmailIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
      />
    </svg>
  );

  const LockIcon = () => (
    <svg
      className="w-5 h-5"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={1.5}
        d="M12 15v2m-6 4h12a2 2 0 002-2v-6a2 2 0 00-2-2H6a2 2 0 00-2 2v6a2 2 0 002 2zm10-10V7a4 4 0 00-8 0v4h8z"
      />
    </svg>
  );

  const ArrowLeftIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M10 19l-7-7m0 0l7-7m-7 7h18"
      />
    </svg>
  );

  const ArrowRightIcon = () => (
    <svg
      className="w-4 h-4"
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        strokeWidth={2}
        d="M14 5l7 7m0 0l-7 7m7-7H3"
      />
    </svg>
  );

  return (
    <div className="h-screen w-full flex items-center justify-center p-8 bg-white">
      <div className="w-full max-w-[420px]">
        {/* Logo */}
        <div className="mb-8">
          <img
            src={pabLoginLogo}
            alt="Garage360 Logo"
            className="h-12 w-auto"
          />
        </div>

        {/* Header */}
        <div className="mb-6">
          <h2 className="text-3xl font-bold text-gray-800 mb-2">
            Forgot password?
          </h2>
          <p className="text-gray-500 text-sm">
            {stage === "email" && "Please enter your registered email address"}
            {stage === "code" && "Enter the OTP code sent to your email"}
            {stage === "password" && "Enter your new password"}
          </p>
        </div>

        {/* Message Display */}
        {message && (
          <div
            className={`mb-5 p-3 rounded-lg text-sm ${
              messageType === "success"
                ? "bg-green-50 border border-green-200 text-green-700"
                : "bg-red-50 border border-red-200 text-red-700"
            }`}
          >
            {message}
          </div>
        )}

        {/* Lockout Message */}
        {isLockedOut && lockoutUntil && (
          <div className="mb-5 p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-yellow-700 text-sm">
            Too many attempts. Please try again after{" "}
            {Math.ceil(
              (lockoutUntil.getTime() - new Date().getTime()) / (1000 * 60)
            )}{" "}
            minute
            {Math.ceil(
              (lockoutUntil.getTime() - new Date().getTime()) / (1000 * 60)
            ) > 1
              ? "s"
              : ""}
            .
          </div>
        )}

        {/* Stage 1: Email Input */}
        {stage === "email" && (
          <div>
            <div className="mb-6">
              <label
                htmlFor="email"
                className="block text-sm font-medium text-gray-800 mb-2"
              >
                Email Address
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <EmailIcon />
                </div>
                <input
                  type="email"
                  id="email"
                  value={formData.EmailId}
                  onChange={(e) => {
                    setFormData({ ...formData, EmailId: e.target.value });
                    setMessage("");
                    setMessageType("");
                  }}
                  className="w-full py-3 pl-11 pr-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-[3px] focus:ring-green-500/10 transition-all text-[0.95rem]"
                  placeholder="Enter your email"
                  required
                  disabled={isLoading || isLockedOut}
                  autoFocus
                />
              </div>
            </div>

            <div className="flex items-center gap-4">
              <button
                type="button"
                onClick={() => {
                  if (onBackToLogin) {
                    onBackToLogin();
                  } else {
                    window.location.href = "/";
                  }
                }}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors"
                disabled={isLoading}
              >
                <ArrowLeftIcon />
                <span className="text-sm font-medium">Back to login</span>
              </button>

              <button
                type="button"
                onClick={handleRequestOTP}
                disabled={isLoading || isLockedOut}
                className={`flex-1 py-3.5 px-4 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all ${
                  isLoading || isLockedOut
                    ? "opacity-70 cursor-not-allowed"
                    : ""
                }`}
              >
                {isLoading ? (
                  <>
                    <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    Send OTP
                    <ArrowRightIcon />
                  </>
                )}
              </button>
            </div>
          </div>
        )}

        {/* Stage 2: OTP Validation */}
        {stage === "code" && (
          <div>
            <div className="mb-6">
              <label
                htmlFor="otp"
                className="block text-sm font-medium text-gray-800 mb-2"
              >
                OTP Code
              </label>
              <input
                type="text"
                id="otp"
                value={formData.ValidationCode}
                onChange={(e) => {
                  setFormData({
                    ...formData,
                    ValidationCode: e.target.value,
                  });
                  setMessage("");
                  setMessageType("");
                }}
                className="w-full py-3 px-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-[3px] focus:ring-green-500/10 transition-all text-[0.95rem] text-center text-lg tracking-widest"
                placeholder="Enter OTP code"
                required
                disabled={isLoading}
                autoFocus
                maxLength={12}
              />
            </div>

            <div className="mb-4 flex items-center justify-between">
              <button
                type="button"
                onClick={() => setStage("email")}
                className="flex items-center gap-2 text-gray-600 hover:text-gray-800 transition-colors text-sm"
                disabled={isLoading}
              >
                <ArrowLeftIcon />
                <span>Change email</span>
              </button>

              <button
                type="button"
                onClick={handleResendOTP}
                disabled={
                  isLoading ||
                  isCountingDown ||
                  resendAttempts >= MAX_RESEND_ATTEMPTS ||
                  isLockedOut
                }
                className={`text-sm font-medium transition-colors ${
                  isLoading ||
                  isCountingDown ||
                  resendAttempts >= MAX_RESEND_ATTEMPTS ||
                  isLockedOut
                    ? "text-gray-400 cursor-not-allowed"
                    : "text-green-600 hover:underline"
                }`}
              >
                {isCountingDown
                  ? `Resend OTP (${secondsLeft}s)`
                  : "Resend OTP"}
              </button>
            </div>

            <button
              type="button"
              onClick={handleValidateOTP}
              disabled={isLoading}
              className={`w-full py-3.5 px-4 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" />
                  Validating...
                </>
              ) : (
                <>
                  Validate OTP
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </div>
        )}

        {/* Stage 3: Password Reset */}
        {stage === "password" && (
          <div>
            <div className="mb-5">
              <label
                htmlFor="password"
                className="block text-sm font-medium text-gray-800 mb-2"
              >
                New Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <LockIcon />
                </div>
                <input
                  type="password"
                  id="password"
                  value={formData.Password}
                  onChange={(e) => {
                    setFormData({ ...formData, Password: e.target.value });
                    setMessage("");
                    setMessageType("");
                  }}
                  className="w-full py-3 pl-11 pr-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-[3px] focus:ring-green-500/10 transition-all text-[0.95rem]"
                  placeholder="Enter new password"
                  required
                  disabled={isLoading}
                  autoFocus
                />
              </div>
            </div>

            <div className="mb-6">
              <label
                htmlFor="confirmPassword"
                className="block text-sm font-medium text-gray-800 mb-2"
              >
                Confirm Password
              </label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-4 flex items-center pointer-events-none text-gray-400">
                  <LockIcon />
                </div>
                <input
                  type="password"
                  id="confirmPassword"
                  value={formData.ConfirmPassword}
                  onChange={(e) => {
                    setFormData({
                      ...formData,
                      ConfirmPassword: e.target.value,
                    });
                    setMessage("");
                    setMessageType("");
                  }}
                  className="w-full py-3 pl-11 pr-4 border border-gray-200 rounded-lg bg-gray-50 text-gray-800 placeholder-gray-400 focus:outline-none focus:border-green-500 focus:bg-white focus:ring-[3px] focus:ring-green-500/10 transition-all text-[0.95rem]"
                  placeholder="Confirm new password"
                  required
                  disabled={isLoading}
                />
              </div>
            </div>

            <button
              type="button"
              onClick={handleResetPassword}
              disabled={isLoading}
              className={`w-full py-3.5 px-4 bg-gray-800 hover:bg-gray-900 text-white font-semibold rounded-lg flex items-center justify-center gap-2 transition-all ${
                isLoading ? "opacity-70 cursor-not-allowed" : ""
              }`}
            >
              {isLoading ? (
                <>
                  <div className="w-4 h-4 border-2 border-transparent border-t-current rounded-full animate-spin" />
                  Resetting...
                </>
              ) : (
                <>
                  Reset Password
                  <ArrowRightIcon />
                </>
              )}
            </button>
          </div>
        )}

        {/* Footer */}
        <div className="text-center mt-8 text-sm text-gray-400">
          Powered by <strong className="text-gray-600">Techmind</strong> •
          Secure Connection
        </div>
      </div>
    </div>
  );
};
